import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Store, Package, Receipt, Percent, BarChart3, PlusCircle, ArrowRight, Sparkles, Star, Pencil, ExternalLink, Eye, BadgeCheck, MessageSquareWarning, Loader2, PauseCircle, PlayCircle } from "lucide-react";
import { toast } from "sonner";
import {
  CATEGORIAS_DIGITALES,
  MKT_GOLD,
  MKT_TINT,
} from "../../lib/marketplace";
import { apiGetTienda, apiGetProductos, apiGetConfiguracion, apiGuardarConfiguracion } from "../../lib/marketplaceApi";
import { EstadoBadge } from "./mktui";
import AlmacenamientoWidget from "./AlmacenamientoWidget";
import MembresiaCard from "./MembresiaCard";

const catLabel = (id) => CATEGORIAS_DIGITALES.find((c) => c.id === id)?.label || id;

function SinTienda() {
  return (
    <div className="space-y-6" data-testid="mi-tienda-vacia">
      <section className="rounded-2xl border border-slate-200 bg-white shadow-soft overflow-hidden">
        <div className="p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-6">
          <div className="flex-1">
            <p className="text-[11px] font-bold uppercase tracking-[0.2em]" style={{ color: MKT_GOLD }}>Bienvenido</p>
            <h2 className="mt-1 text-xl md:text-2xl font-extrabold text-slate-900">Empieza a vender en ConstruLink Marketplace</h2>
            <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-50 text-amber-700 text-xs font-semibold border border-amber-200" data-testid="estado-tienda">
              <span className="w-2 h-2 rounded-full bg-amber-500" /> Aún no has creado tu tienda
            </div>
            <p className="mt-3 text-sm text-slate-500 max-w-lg">
              Crea tu tienda digital, elige tus categorías y comienza a ofrecer plantillas, software, recursos BIM/CAD y más.
            </p>
            <Link to="/marketplace/crear-tienda" data-testid="crear-mi-tienda-btn" className="mt-5 inline-flex items-center gap-2 px-6 py-3 rounded-xl text-white font-bold shadow-sm hover:brightness-110 transition-all" style={{ backgroundColor: MKT_GOLD }}>
              <PlusCircle size={18} /> Crear mi tienda
            </Link>
          </div>
          <div className="w-full md:w-64 flex-shrink-0">
            <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 h-40 flex flex-col items-center justify-center text-slate-400">
              <Store size={40} />
              <p className="mt-2 text-xs font-semibold">Vista previa de tu tienda</p>
            </div>
          </div>
        </div>
      </section>

      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold uppercase tracking-widest text-slate-500">Categorías de productos digitales</h3>
          <Link to="/marketplace/crear-tienda" className="inline-flex items-center gap-1 text-xs font-semibold" style={{ color: MKT_GOLD }} data-testid="ver-categorias-link">
            Elegir categorías <ArrowRight size={13} />
          </Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="acceso-categorias">
          {CATEGORIAS_DIGITALES.map((c) => (
            <Link key={c.id} to="/marketplace/crear-tienda" className="rounded-xl border border-slate-200 bg-white p-3 flex flex-col items-center text-center gap-2 hover:-translate-y-0.5 hover:shadow-soft transition-all" data-testid={`cat-acceso-${c.id}`}>
              <span className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: MKT_TINT, color: MKT_GOLD }}>
                <c.Icon size={17} />
              </span>
              <span className="text-[11px] font-semibold text-slate-700 leading-tight">{c.label}</span>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function MiTienda() {
  const navigate = useNavigate();
  const [tienda, setTienda] = useState(undefined);
  const [productos, setProductos] = useState([]);
  const [reactivando, setReactivando] = useState(false);

  const reactivar = async () => {
    setReactivando(true);
    try {
      const cfg = await apiGetConfiguracion();
      await apiGuardarConfiguracion({ ...cfg, vacaciones: false });
      setTienda((t) => ({ ...t, vacaciones: false }));
      toast.success("Tu tienda está activa de nuevo y visible en el catálogo.");
    } catch (e) { toast.error("No se pudo reactivar la tienda. Inténtalo de nuevo."); }
    finally { setReactivando(false); }
  };

  useEffect(() => {
    (async () => {
      try {
        const [t, p] = await Promise.all([apiGetTienda(), apiGetProductos()]);
        setTienda(t);
        setProductos(p);
      } catch (e) {
        setTienda(null);
      }
    })();
  }, []);

  if (tienda === undefined) {
    return <div className="flex items-center justify-center py-24 text-slate-500" data-testid="mi-tienda-loading"><Loader2 className="animate-spin mr-2" /> Cargando…</div>;
  }
  if (!tienda) return <SinTienda />;

  const resumen = [
    { key: "productos", label: "Productos", value: String(productos.length), Icon: Package },
    { key: "ventas", label: "Ventas del mes", value: "S/ 0.00", Icon: Receipt },
    { key: "comisiones", label: "Comisiones", value: "S/ 0.00", Icon: Percent },
    { key: "visitas", label: "Visitas", value: "0", Icon: BarChart3 },
  ];

  return (
    <div className="space-y-6" data-testid="mi-tienda">
      {/* Perfil de tienda */}
      <section className="rounded-2xl border border-slate-200 bg-white shadow-soft overflow-hidden">
        <div className="h-36 md:h-44 relative" style={{ background: tienda.portada ? undefined : `linear-gradient(135deg, ${MKT_GOLD}, #B45309)` }}>
          {tienda.portada && <img src={tienda.portada} alt="portada" className="w-full h-full object-cover" />}
        </div>
        <div className="px-6 pb-6">
          <div className="flex flex-col sm:flex-row sm:items-end gap-4 -mt-10">
            <div className="w-20 h-20 rounded-2xl border-4 border-white bg-white shadow-md overflow-hidden flex items-center justify-center flex-shrink-0" data-testid="mi-tienda-logo">
              {tienda.logo ? <img src={tienda.logo} alt="logo" className="w-full h-full object-cover" /> : <Store size={30} style={{ color: MKT_GOLD }} />}
            </div>
            <div className="flex-1 sm:pb-1">
              <div className="flex items-center gap-3 flex-wrap">
                <h2 className="text-xl md:text-2xl font-extrabold text-slate-900" data-testid="mi-tienda-nombre">{tienda.nombre}</h2>
                <EstadoBadge estado={tienda.estado} tipo="tienda" testid="mi-tienda-estado" />
                {tienda.vacaciones && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold text-amber-700 bg-amber-50 border border-amber-200" data-testid="mi-tienda-en-pausa">
                    <PauseCircle size={13} /> En pausa
                  </span>
                )}
                {tienda.verificado && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200" data-testid="mi-tienda-verificado">
                    <BadgeCheck size={13} /> Vendedor verificado
                  </span>
                )}
              </div>
              <p className="text-sm text-slate-500 mt-0.5">
                por <b className="text-slate-700">{tienda.vendedor}</b>
                {tienda.ciudad && ` · ${tienda.ciudad}`}{tienda.pais && `, ${tienda.pais}`}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {tienda.vacaciones && (
                <button onClick={reactivar} disabled={reactivando} data-testid="mi-tienda-reactivar" className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-white font-semibold text-sm hover:brightness-110 transition-all disabled:opacity-60" style={{ backgroundColor: "#16A34A" }}>
                  {reactivando ? <Loader2 size={15} className="animate-spin" /> : <PlayCircle size={15} />} Reactivar
                </button>
              )}
              <button onClick={() => navigate("/marketplace/crear-tienda")} data-testid="editar-tienda-btn" className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-slate-300 text-slate-700 font-semibold text-sm hover:bg-slate-100 transition-colors">
                <Pencil size={15} /> Editar tienda
              </button>
              <button onClick={() => navigate("/marketplace/tienda-publica")} data-testid="ver-tienda-publica-btn" className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-white font-semibold text-sm shadow-sm hover:brightness-110 transition-all" style={{ backgroundColor: MKT_GOLD }}>
                <ExternalLink size={15} /> Ver mi tienda pública
              </button>
            </div>
          </div>

          {tienda.descripcion && <p className="mt-4 text-sm text-slate-600 max-w-2xl">{tienda.descripcion}</p>}

          {tienda.comentarios?.length > 0 && (
            <div className="mt-4 rounded-xl bg-amber-50 border border-amber-200 p-3" data-testid="mi-tienda-comentarios">
              <p className="text-xs font-bold text-amber-800 inline-flex items-center gap-1"><MessageSquareWarning size={13} /> Comentarios de revisión</p>
              <ul className="mt-1 space-y-1">
                {tienda.comentarios.map((cm, i) => (
                  <li key={i} className="text-xs text-amber-700">• {cm.texto}</li>
                ))}
              </ul>
            </div>
          )}

          {tienda.categorias?.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2" data-testid="mi-tienda-categorias">
              {tienda.categorias.map((id) => (
                <span key={id} className="text-xs font-semibold px-3 py-1 rounded-full" style={{ backgroundColor: MKT_TINT, color: "#92400E" }}>
                  {catLabel(id)}
                </span>
              ))}
            </div>
          )}

          <div className="mt-5 flex flex-wrap items-center gap-6 text-sm">
            <span className="inline-flex items-center gap-1.5 text-slate-600"><Package size={15} style={{ color: MKT_GOLD }} /> <b>{productos.length}</b> productos</span>
            <span className="inline-flex items-center gap-1.5 text-slate-600"><Star size={15} style={{ color: MKT_GOLD }} /> <b>—</b> calificación <span className="text-xs text-slate-400">(preliminar)</span></span>
            <span className="inline-flex items-center gap-1.5 text-slate-600"><Receipt size={15} style={{ color: MKT_GOLD }} /> <b>0</b> ventas <span className="text-xs text-slate-400">(preliminar)</span></span>
          </div>
        </div>
      </section>

      {/* Resumen */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold uppercase tracking-widest text-slate-500">Resumen</h3>
          <span className="text-[11px] font-semibold text-slate-400">Datos preliminares</span>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="resumen-tienda">
          {resumen.map((r) => (
            <div key={r.key} className="rounded-2xl border border-slate-200 bg-white shadow-soft p-5">
              <span className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: MKT_TINT, color: MKT_GOLD }}>
                <r.Icon size={18} />
              </span>
              <p className="mt-3 text-2xl font-extrabold text-slate-900">{r.value}</p>
              <p className="text-xs text-slate-500 mt-0.5">{r.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Membresía y comisión */}
      <MembresiaCard />

      {/* Almacenamiento y cuotas */}
      <AlmacenamientoWidget />

      {/* CTA productos */}
      <section className="rounded-2xl border border-dashed border-amber-300 bg-amber-50/50 p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Sparkles size={22} style={{ color: MKT_GOLD }} />
          <div>
            <p className="font-bold text-slate-900">Publica tu primer producto digital</p>
            <p className="text-sm text-slate-500">Plantillas, software, recursos BIM/CAD y más.</p>
          </div>
        </div>
        <Link to="/marketplace/productos" data-testid="ir-mis-productos-btn" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-white font-bold shadow-sm hover:brightness-110 transition-all" style={{ backgroundColor: MKT_GOLD }}>
          <Eye size={16} /> Ir a Mis Productos
        </Link>
      </section>
    </div>
  );
}
